import { useCallback, useState } from 'react'
import { handDemoSessionToTab, openDemoSessionTab, tellTabItFailed } from '../demoSession.js'
import type { DemoSessionResponse, OpenDemoSessionInput } from '../index.js'
import { useEndpointHints } from '../../core/index.js'

/**
 * Opens a demo session in a new tab: the tab first, synchronously, so the
 * browser treats it as asked for; then the API; then the hand-off. One
 * hook for the admin's "Open as…" and the demo operator's "View as…", which
 * differ only in which endpoint answers. The new tab shows the endpoint
 * paths when this one does, which is only ever the admin area.
 */
export function useDemoSessionLauncher(open: (input: OpenDemoSessionInput) => Promise<DemoSessionResponse>) {
  const showsEndpoints = useEndpointHints()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const launch = useCallback(
    async (input: OpenDemoSessionInput) => {
      setError(null)
      const tab = openDemoSessionTab(input.as)
      if (!tab) {
        setError('The browser blocked the new tab. Allow pop-ups for this site and try again.')
        return
      }
      setPending(true)
      try {
        const response = await open(input)
        await handDemoSessionToTab(tab, response, showsEndpoints)
      } catch (caught) {
        const message = caught instanceof Error && caught.message ? caught.message : 'The demo session could not be opened.'
        tellTabItFailed(tab, message)
        setError(message)
      } finally {
        setPending(false)
      }
    },
    [open, showsEndpoints],
  )

  return { launch, pending, error }
}
